/**
 * 문제집 배정 모달 컴포넌트
 * - Transfer 컴포넌트로 학생 선택
 * - 제출 기한 설정 (optional)
 * - 벌크 배정 지원
 */

import React, { useState, useEffect } from 'react';
import { Modal, Transfer, message, Space } from 'antd';
import type { TransferProps } from 'antd';
import { userService } from '../../services/userService';
import { assignmentService } from '../../services/assignmentService';
import { useAuthStore } from '../../store/authStore';
import type { User } from '../../types/user';
import type { Workbook } from '../../types/workbook';

interface AssignWorkbookModalProps {
  open: boolean;
  workbook: Workbook | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * AssignWorkbookModal 컴포넌트
 * 문제집을 학생에게 배정하는 모달
 */
export const AssignWorkbookModal: React.FC<AssignWorkbookModalProps> = ({
  open,
  workbook,
  onClose,
  onSuccess,
}) => {
  const { user: currentUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allStudents, setAllStudents] = useState<User[]>([]);
  const [assignedStudentIds, setAssignedStudentIds] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  /**
   * 모달 열릴 때 학생 목록과 기존 배정 정보 조회
   */
  useEffect(() => {
    if (open && workbook) {
      fetchStudents();
      fetchAssignments();
    }
  }, [open, workbook]);

  /**
   * 학생 목록 조회
   * 강사인 경우: 담당 학생만 조회
   * 그룹 관리자인 경우: 그룹 내 모든 학생 조회
   */
  const fetchStudents = async () => {
    setLoading(true);
    try {
      if (currentUser?.role === 'instructor') {
        // 강사는 담당 학생만 조회
        const myStudents = await userService.getMyStudents();
        setAllStudents(myStudents);
      } else {
        // 그룹 관리자 또는 관리자는 모든 학생 조회
        const response = await userService.getUsers({
          role: 'student',
          page: 1,
          page_size: 100,
        });
        setAllStudents(response.users);
      }
    } catch (error) {
      message.error('학생 목록 조회 실패');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 이미 배정된 학생 목록 조회
   */
  const fetchAssignments = async () => {
    if (!workbook) return;

    try {
      const assignments = await assignmentService.getWorkbookAssignments(workbook.name);
      const assignedIds = assignments.map((a) => a.student_id);
      setAssignedStudentIds(assignedIds);
      setSelectedKeys(assignedIds); // 기본값으로 이미 배정된 학생 선택
    } catch (error) {
      console.error('기존 배정 목록 조회 실패:', error);
      // 에러는 무시 (배정 이력이 없을 수 있음)
    }
  };

  /**
   * Transfer onChange 핸들러
   */
  const handleChange: TransferProps['onChange'] = (newTargetKeys) => {
    setSelectedKeys(newTargetKeys as string[]);
  };

  /**
   * 배정 제출
   * - 새로 배정할 학생: selectedKeys에 있지만 assignedStudentIds에 없는 학생
   * - 배정 해제할 학생: assignedStudentIds에 있지만 selectedKeys에 없는 학생
   */
  const handleSubmit = async () => {
    if (!workbook) return;

    // 새로 배정할 학생 (기존에 없던 학생)
    const newStudentIds = selectedKeys.filter((id) => !assignedStudentIds.includes(id));
    // 배정 해제할 학생 (기존에 있었지만 선택 해제된 학생)
    const removeStudentIds = assignedStudentIds.filter((id) => !selectedKeys.includes(id));

    if (newStudentIds.length === 0 && removeStudentIds.length === 0) {
      message.warning('변경할 내용이 없습니다');
      return;
    }

    setSubmitting(true);
    try {
      // 1. 새로운 학생 배정
      if (newStudentIds.length > 0) {
        await assignmentService.assignWorkbook(workbook.name, {
          student_ids: newStudentIds,
        });
      }

      // 2. 배정 해제 (각 학생별로 DELETE 호출)
      if (removeStudentIds.length > 0) {
        await Promise.all(
          removeStudentIds.map((studentId) =>
            assignmentService.deleteAssignment(workbook.name, studentId)
          )
        );
      }

      // 결과 메시지 생성
      const messages: string[] = [];
      if (newStudentIds.length > 0) {
        messages.push(`${newStudentIds.length}명 배정`);
      }
      if (removeStudentIds.length > 0) {
        messages.push(`${removeStudentIds.length}명 해제`);
      }
      message.success(messages.join(', ') + ' 완료');

      onSuccess();
      handleClose();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '배정 처리 실패');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * 모달 닫기
   */
  const handleClose = () => {
    setSelectedKeys([]);
    onClose();
  };

  return (
    <Modal
      title={`문제집 배정 - ${workbook?.name || ''}`}
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={submitting}
      okText="배정"
      cancelText="취소"
      width={700}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* 학생 선택 (Transfer) */}
        <div>
          <label style={{ display: 'block', marginBottom: 8 }}>
            배정할 학생 선택
          </label>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>로딩 중...</div>
          ) : (
            <Transfer
              dataSource={allStudents.map((student) => ({
                key: student.id,
                title: `${student.name} (${student.email})`,
                description: student.phone || '',
              }))}
              titles={['미배정 학생', '배정할 학생']}
              targetKeys={selectedKeys}
              onChange={handleChange}
              render={(item) => item.title}
              listStyle={{
                width: 300,
                height: 400,
              }}
              showSearch
              filterOption={(inputValue, option) =>
                option.title.toLowerCase().includes(inputValue.toLowerCase())
              }
            />
          )}
        </div>

        {/* 안내 문구 */}
        <div style={{ color: '#666', fontSize: '12px' }}>
          * 이미 배정된 학생은 제외하고 새로운 학생만 배정됩니다.
          <br />* 최대 100명까지 선택할 수 있습니다.
        </div>
      </Space>
    </Modal>
  );
};
